// src/components/UserDashboard.js
import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Paper,
  TextField,
  Button,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  IconButton,
  CircularProgress,
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';

import {
  StyledMainContainer,
  StyledHeaderWrapper,
  StyledPageTitle,
  StyledTableContainer,
  StyledTableHeaderCell,
  StyledTableLinkCell,
  StyledNoUsersCell,
  StyledPaper, 
  StyledActionsBar
} from './StyleComponents';

import api from './api';
import UserDetailForm from './UserDetail';
import Header from './Header';

function UserDashboard() {
  const [searchTerm, setSearchTerm] = useState('');
  const [openUserDetail, setOpenUserDetail] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);

   const fetchUsers = async () => {
  try {
     setLoading(true);
     const { data } = await api.get('/users/');
     setUsers(data || []);
   } catch (error) {
     console.error('Error al obtener usuarios:', error);
     setUsers([]);
   } finally {
     setLoading(false);
   }
 };

  useEffect(() => {
    fetchUsers();
  }, []);

  const filteredUsers = users.filter(user => {
    const term = searchTerm.toLowerCase();
    const statusText = user.is_active ? 'activo' : 'inactivo';
    return (
      (user.nombre?.toLowerCase().includes(term)) ||
      (user.a_paterno?.toLowerCase().includes(term)) ||
      (user.username?.toLowerCase().includes(term)) ||
      (user.role?.toLowerCase().includes(term)) ||
      (statusText.includes(term))
    );
  });

  const activeUsers = users.filter(user => user.is_active === true).length;
  const inactiveUsers = users.filter(user => user.is_active === false).length;

  const handleSearchChange = (event) => {
    setSearchTerm(event.target.value);
  };
  
  const handleCreateUserClick = () => {
    setSelectedUser(null);
    setOpenUserDetail(true);
  };

  const handleEditClick = (user) => {
    setSelectedUser(user);
    setOpenUserDetail(true);
  };

  const handleCloseUserDetail = () => {
    setOpenUserDetail(false);
    setSelectedUser(null);
  };

  const handleSaveUser = () => {
    fetchUsers();
    handleCloseUserDetail();
  };

  return (
    <StyledMainContainer>
      <Header />
      <StyledHeaderWrapper>
        <StyledPageTitle variant="h4" component="h1" gutterBottom>
          Usuarios
        </StyledPageTitle>
      </StyledHeaderWrapper>

      <Box sx={{ display: 'flex', gap: 3, mb: 4 }}>
        <Paper elevation={1} sx={{ p: 3, textAlign: 'center', minWidth: 150, borderRadius: 2 }}>
          <Typography variant="h3" color="primary" sx={{ fontWeight: 'bold' }}>
            {activeUsers}
          </Typography>
          <Typography variant="subtitle1" color="text.secondary">
            Activos
          </Typography>
        </Paper>
        <Paper elevation={1} sx={{ p: 3, textAlign: 'center', minWidth: 150, borderRadius: 2 }}>
          <Typography variant="h3" color="error" sx={{ fontWeight: 'bold' }}>
            {inactiveUsers}
          </Typography>
          <Typography variant="subtitle1" color="text.secondary">
            Inactivos
          </Typography>
        </Paper>
      </Box>

      <StyledActionsBar>
        <TextField
          label="Filtrar por nombre, rol, planta, estatus"
          variant="outlined"
          fullWidth
          size="small"
          value={searchTerm}
          onChange={handleSearchChange}
          InputProps={{
            endAdornment: (
              <IconButton>
                <SearchIcon />
              </IconButton>
            ),
          }}
        />
        <Button variant="contained" sx={{ whiteSpace: 'nowrap', minWidth: '150px' }} onClick={handleCreateUserClick}>
          Crear Usuario
        </Button>
        </StyledActionsBar>
        {loading ? (
   <Box display="flex" justifyContent="center" alignItems="center" height="200px">
     <CircularProgress />
   </Box>
 ) : (

      <StyledTableContainer component={StyledPaper} elevation={1}> {/* O component={Paper} si no estilizaste Paper */}
        <Table>
          <TableHead>
            <TableRow>
              <StyledTableHeaderCell>Nombre</StyledTableHeaderCell>
              <StyledTableHeaderCell>Nombre de usuario</StyledTableHeaderCell>
              <StyledTableHeaderCell>Rol</StyledTableHeaderCell>
              <StyledTableHeaderCell>Plantas asignadas</StyledTableHeaderCell>
              <StyledTableHeaderCell>Estatus</StyledTableHeaderCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {filteredUsers.length > 0 ? (
              filteredUsers.map((user) => (
                <TableRow key={user.id}>
                  <StyledTableLinkCell onClick={() => handleEditClick(user)}>
                    {user.nombre} {user.a_paterno} {user.a_materno}
                  </StyledTableLinkCell>
                  <TableCell>{user.username}</TableCell>
                  <TableCell>{user.role}</TableCell>
                  <TableCell>{(user.assignedPlants || []).join(', ')}</TableCell>
                  <TableCell>{user.is_active ? 'Activo' : 'Inactivo'}</TableCell>
                </TableRow>
              ))
            ) : (
              <TableRow>
                <StyledNoUsersCell colSpan={5}>No se encontraron usuarios.</StyledNoUsersCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </StyledTableContainer>)}
      
      <UserDetailForm
        open={openUserDetail}
        onClose={handleCloseUserDetail}
        user={selectedUser}
        onSave={handleSaveUser}
      />
    </StyledMainContainer>
  );
}

export default UserDashboard;
